import React, {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import ReactFamilyTree from "react-family-tree";
import Swal from "sweetalert2";
import PinchZoomPanContainer from "../FamilyTree/PinchZoomPanContainer";
import MemberEditor from "../FamilyTree/MemberEditor";
import ImageGallery from "../FamilyTree/ImageGallery";
import SearchBar from "./SearchBar";
import apiService from "../../api/apiService";
import "../../scss/familyTree/reactFamilyTreeView.scss";
import "../../scss/familyTree/searchBar.scss";

/**
 * ReactFamilyTreeView Component
 * Uses react-family-tree library for proper connection rendering
 * Shows husband-wife relationships and parent-child connections
 */
const ReactFamilyTreeView = ({
  members = [],
  onMemberUpdate = () => {},
  focusMemberId = null,
  title = "Family Tree",
  showToolbar = true,
  slug = "",
  parentLoading = false,
  onShowHelp = null,
  familyTree = null,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [editingMember, setEditingMember] = useState(null);
  const [galleryMember, setGalleryMember] = useState(null);
  const [selectedMemberId, setSelectedMemberId] = useState(focusMemberId);

  // Initialize rootId from URL params, familyTree.rootMemberId, or null
  const getInitialRootId = () => {
    const urlRootId = searchParams.get("rootId");
    if (urlRootId) return urlRootId;
    if (familyTree?.rootMemberId) return familyTree.rootMemberId;
    return null;
  };

  const [rootId, setRootId] = useState(getInitialRootId());
  const [addingMemberFor, setAddingMemberFor] = useState(null);
  const [newMemberData, setNewMemberData] = useState({
    name: "",
    gender: "",
    birthDate: "",
    birthPlace: "",
    deathDate: "",
    deathPlace: "",
    occupation: "",
    description: "",
    relationshipType: "",
    relatedMemberId: "",
    images: [],
  });
  const [addMemberMessage, setAddMemberMessage] = useState({
    type: "",
    text: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showAddMemberGallery, setShowAddMemberGallery] = useState(false);
  const pinchZoomRef = useRef(null);
  const containerRef = useRef(null);
  const hasScrolledToTop = useRef(false);
  const addMemberModalRef = useRef(null);
  const searchBarRef = useRef(null);

  const WIDTH = 260;
  const HEIGHT = 280;

  /**
   * Update rootId when familyTree data is loaded (only if rootId not set from URL)
   */
  useEffect(() => {
    const urlRootId = searchParams.get("rootId");
    if (!urlRootId && familyTree?.rootMemberId && !rootId) {
      setRootId(familyTree.rootMemberId);
    }
  }, [familyTree, searchParams, rootId]);

  /**
   * Update URL when rootId changes
   */
  useEffect(() => {
    if (rootId) {
      const currentRootId = searchParams.get("rootId");
      if (currentRootId !== rootId) {
        setSearchParams({ rootId }, { replace: true });
      }
    }
  }, [rootId, searchParams, setSearchParams]);

  /**
   * Sync galleryMember with updated member from members array
   * This ensures the gallery always shows the latest data after updates
   */
  useEffect(() => {
    if (galleryMember && members.length > 0) {
      const updatedMember = members.find((m) => m.id === galleryMember.id);
      if (
        updatedMember &&
        JSON.stringify(updatedMember.images) !==
          JSON.stringify(galleryMember.images)
      ) {
        console.log("Syncing galleryMember with updated member", updatedMember);
        setGalleryMember(updatedMember);
      }
    }
  }, [members, galleryMember]);

  /**
   * Scroll to top on component mount to show header and top node
   * Using useLayoutEffect to run before browser paint
   */
  useLayoutEffect(() => {
    const scrollToTop = () => {
      // Scroll window to top
      window.scrollTo(0, 0);

      // Scroll the main container
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
        containerRef.current.scrollLeft = 0;
      }

      // Force scroll on document element and body
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    // Immediate scroll
    scrollToTop();

    // Also scroll after a short delay to catch any late renders
    const timeoutId = setTimeout(scrollToTop, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  /**
   * Focus on selected member by scrolling the view
   */
  useEffect(() => {
    if (selectedMemberId) {
      // Wait for DOM to update, then find and scroll to the selected node
      setTimeout(() => {
        const selectedElement = document.querySelector(`.family-node.selected`);
        if (selectedElement) {
          selectedElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "center",
          });
        }
      }, 100);
    }
  }, [selectedMemberId]);

  /**
   * Reset add member form
   */
  const resetAddMemberForm = () => {
    setAddingMemberFor(null);
    setNewMemberData({
      name: "",
      gender: "",
      birthDate: "",
      birthPlace: "",
      deathDate: "",
      deathPlace: "",
      occupation: "",
      description: "",
      relationshipType: "",
      relatedMemberId: "",
      images: [],
    });
    setAddMemberMessage({ type: "", text: "" });
  };

  /**
   * Transform members data to react-family-tree format
   */
  const transformMembersToNodes = useCallback(() => {
    if (!members || members.length === 0) return [];

    const nodes = [];
    const nodeMap = new Map();

    // First pass: create all nodes with proper initialization
    members.forEach((member) => {
      const node = {
        id: member.id,
        gender:
          member.gender?.toLowerCase() === "male"
            ? "male"
            : member.gender?.toLowerCase() === "female"
            ? "female"
            : "other",
        parents: [],
        children: [],
        siblings: [],
        spouses: [],
        data: {
          ...member,
          name: member?.name || "Unknown",
          birthDate: member?.birthDate,
          deathDate: member?.deathDate,
          images: member.images || [],
        },
      };

      nodes.push(node);
      nodeMap.set(member.id, node);
    });

    // Second pass: establish relationships
    members.forEach((member) => {
      const node = nodeMap.get(member.id);
      if (!node) return;

      // Add parents (handle both string IDs and {id, type} objects)
      if (member.parents) {
        const parentArray = Array.isArray(member.parents)
          ? member.parents
          : [member.parents];
        node.parents = parentArray
          .map((p) => {
            if (typeof p === "object" && p.id) {
              return nodeMap.has(p.id)
                ? { id: p.id, type: p.type || "blood" }
                : null;
            }
            return nodeMap.has(p) ? { id: p, type: "blood" } : null;
          })
          .filter(Boolean);
      }

      // Add children (handle both string IDs and {id, type} objects)
      if (member.children) {
        const childArray = Array.isArray(member.children)
          ? member.children
          : [member.children];
        node.children = childArray
          .map((c) => {
            if (typeof c === "object" && c.id) {
              return nodeMap.has(c.id)
                ? { id: c.id, type: c.type || "blood" }
                : null;
            }
            return nodeMap.has(c) ? { id: c, type: "blood" } : null;
          })
          .filter(Boolean);
      }

      // Add spouses (handle both singular 'spouse' and plural 'spouses')
      const spouseList = [];

      // Check for singular spouse
      if (member.spouse && nodeMap.has(member.spouse)) {
        spouseList.push({ id: member.spouse, type: "married" });
      }

      // Check for plural spouses
      if (member.spouses && Array.isArray(member.spouses)) {
        member.spouses.forEach((s) => {
          if (typeof s === "object" && s.id && nodeMap.has(s.id)) {
            spouseList.push({ id: s.id, type: s.type || "married" });
          } else if (typeof s === "string" && nodeMap.has(s)) {
            spouseList.push({ id: s, type: "married" });
          }
        });
      }

      node.spouses = spouseList;

      // Add siblings (handle both string IDs and {id, type} objects)
      if (member.siblings) {
        const siblingArray = Array.isArray(member.siblings)
          ? member.siblings
          : [member.siblings];
        node.siblings = siblingArray
          .map((s) => {
            if (typeof s === "object" && s.id) {
              return nodeMap.has(s.id)
                ? { id: s.id, type: s.type || "blood" }
                : null;
            }
            return nodeMap.has(s) ? { id: s, type: "blood" } : null;
          })
          .filter(Boolean);
      }
    });

    // Ensure spouse relationships are bidirectional
    nodes.forEach((node) => {
      if (node.spouses && node.spouses.length > 0) {
        node.spouses.forEach((spouse) => {
          const spouseNode = nodeMap.get(spouse.id);
          if (spouseNode && !spouseNode.spouses.find((s) => s.id === node.id)) {
            spouseNode.spouses.push({
              id: node.id,
              type: spouse.type || "married",
            });
          }
        });
      }
    });

    // Ensure parent-child relationships are bidirectional
    nodes.forEach((node) => {
      // For each child, ensure parent has child in their children array
      if (node.children && node.children.length > 0) {
        node.children.forEach((child) => {
          const childNode = nodeMap.get(child.id);
          if (childNode && !childNode.parents.find((p) => p.id === node.id)) {
            childNode.parents.push({
              id: node.id,
              type: child.type || "blood",
            });
          }
        });
      }

      // For each parent, ensure they have this node in their children array
      if (node.parents && node.parents.length > 0) {
        node.parents.forEach((parent) => {
          const parentNode = nodeMap.get(parent.id);
          if (
            parentNode &&
            !parentNode.children.find((c) => c.id === node.id)
          ) {
            parentNode.children.push({
              id: node.id,
              type: parent.type || "blood",
            });
          }
        });
      }
    });

    // Validate all nodes have required arrays (prevent undefined errors)
    nodes.forEach((node) => {
      node.parents = node.parents || [];
      node.children = node.children || [];
      node.siblings = node.siblings || [];
      node.spouses = node.spouses || [];
    });

    // CRITICAL: Create a Set of all valid node IDs for fast lookup
    const validNodeIds = new Set(nodes.map((n) => n.id));

    // Final cleanup pass: Remove ALL invalid references
    nodes.forEach((node) => {
      // Remove any parent IDs that don't exist in our node list
      node.parents = node.parents.filter((p) => validNodeIds.has(p.id));

      // Remove any child IDs that don't exist in our node list
      node.children = node.children.filter((c) => validNodeIds.has(c.id));

      // Remove any spouse IDs that don't exist in our node list
      node.spouses = node.spouses.filter((s) => validNodeIds.has(s.id));

      // Remove any sibling IDs that don't exist in our node list
      node.siblings = node.siblings.filter((s) => validNodeIds.has(s.id));
    });

    // Find root (oldest ancestor without parents with most descendants)
    const nodesWithoutParents = nodes.filter(
      (n) => !n.parents || n.parents.length === 0
    );

    // Count total descendants for each potential root
    const countDescendants = (nodeId, visited = new Set()) => {
      if (visited.has(nodeId)) return 0;
      visited.add(nodeId);

      const node = nodeMap.get(nodeId);
      if (!node) return 0;

      let count = node.children?.length || 0;
      node.children?.forEach((child) => {
        count += countDescendants(child.id, visited);
      });

      return count;
    };

    // Sort by descendant count (descending) to find the true root
    let root = nodesWithoutParents.sort((a, b) => {
      const aDescendants = countDescendants(a.id);
      const bDescendants = countDescendants(b.id);
      return bDescendants - aDescendants;
    })[0];

    if (!root && nodes.length > 0) {
      root = nodes[0]; // Fallback to first member
    }

    if (root && !rootId) {
      setRootId(root.id);
    }

    return nodes;
  }, [members, rootId]);

  const nodes = transformMembersToNodes();
  const currentRoot = rootId || (nodes.length > 0 ? nodes[0].id : null);

  /**
   * Check if a member will be rendered by react-family-tree
   * The library only renders nodes connected through parent-child relationships
   * Spouses' siblings and other indirect connections won't be rendered
   */
  const isNodeRenderedInTree = (memberId, rootNodeId, nodeList) => {
    if (!memberId || !rootNodeId || !nodeList || nodeList.length === 0) {
      return false;
    }

    // BFS traversal following only parent-child relationships (what the library renders)
    const visited = new Set();
    const queue = [rootNodeId];

    while (queue.length > 0) {
      const currentId = queue.shift();

      if (currentId === memberId) {
        return true;
      }

      if (visited.has(currentId)) continue;
      visited.add(currentId);

      const currentNode = nodeList.find((n) => n.id === currentId);
      if (!currentNode) continue;

      // Only follow parent-child relationships (what react-family-tree renders)
      // Add children
      if (currentNode.children) {
        currentNode.children.forEach((child) => {
          const childId = typeof child === "object" ? child.id : child;
          if (childId) queue.push(childId);
        });
      }

      // Add parents
      if (currentNode.parents) {
        currentNode.parents.forEach((parent) => {
          const parentId = typeof parent === "object" ? parent.id : parent;
          if (parentId) queue.push(parentId);
        });
      }

      // Add spouses (they are rendered when connected to the current node)
      if (currentNode.spouses) {
        currentNode.spouses.forEach((spouse) => {
          const spouseId = typeof spouse === "object" ? spouse.id : spouse;
          if (spouseId) queue.push(spouseId);
        });
      }
    }

    return false;
  };

  /**
   * Check if a member is in the visible tree starting from the root
   * Uses BFS to traverse the tree and find the member
   */
  const isMemberInVisibleTree = (memberId, rootNodeId, nodeList) => {
    if (!memberId || !rootNodeId || !nodeList || nodeList.length === 0) {
      console.log("isMemberInVisibleTree: Invalid params", {
        memberId,
        rootNodeId,
        nodeListLength: nodeList?.length,
      });
      return false;
    }

    const visited = new Set();
    const queue = [rootNodeId];
    let iterations = 0;
    const maxIterations = 1000; // Safety limit

    while (queue.length > 0 && iterations < maxIterations) {
      iterations++;
      const currentId = queue.shift();

      if (currentId === memberId) {
        console.log(`Found member ${memberId} after ${iterations} iterations`);
        return true;
      }

      if (visited.has(currentId)) continue;
      visited.add(currentId);

      const currentNode = nodeList.find((n) => n.id === currentId);
      if (!currentNode) {
        console.log(`Node not found for ID: ${currentId}`);
        continue;
      }

      // Add all connected nodes to the queue
      // Add children
      if (currentNode.children) {
        currentNode.children.forEach((child) => {
          if (typeof child === "object" && child.id) {
            queue.push(child.id);
          } else {
            queue.push(child);
          }
        });
      }

      // Add parents
      if (currentNode.parents) {
        currentNode.parents.forEach((parent) => {
          if (typeof parent === "object" && parent.id) {
            queue.push(parent.id);
          } else {
            queue.push(parent);
          }
        });
      }

      // Add spouses
      if (currentNode.spouses) {
        currentNode.spouses.forEach((spouse) => {
          if (typeof spouse === "object" && spouse.id) {
            queue.push(spouse.id);
          } else {
            queue.push(spouse);
          }
        });
      }

      // Add siblings
      if (currentNode.siblings) {
        currentNode.siblings.forEach((sibling) => {
          if (typeof sibling === "object" && sibling.id) {
            queue.push(sibling.id);
          } else {
            queue.push(sibling);
          }
        });
      }
    }

    console.log(
      `Member ${memberId} not found in tree after ${iterations} iterations. Visited ${visited.size} nodes.`
    );
    return false;
  };

  /**
   * Generate shareable URL with current root ID
   */
  const getShareableUrl = useCallback(() => {
    const baseUrl = `${window.location.origin}/family/${slug}`;
    if (currentRoot) {
      return `${baseUrl}?rootId=${currentRoot}`;
    }
    return baseUrl;
  }, [slug, currentRoot]);

  /**
   * Scroll to top when nodes are rendered/updated (only once)
   */
  useEffect(() => {
    if (nodes.length > 0 && currentRoot && !hasScrolledToTop.current) {
      hasScrolledToTop.current = true;

      const scrollToTop = () => {
        // Scroll window to top
        window.scrollTo(0, 0);

        // Scroll the main container
        if (containerRef.current) {
          containerRef.current.scrollTop = 0;
          containerRef.current.scrollLeft = 0;
        }

        // Also scroll the tree container
        const treeContainer = document.querySelector(".tree-container");
        if (treeContainer) {
          treeContainer.scrollTop = 0;
          treeContainer.scrollLeft = 0;
        }

        // Force scroll on document element and body
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      };

      // Multiple attempts to ensure scroll happens
      scrollToTop();
      setTimeout(scrollToTop, 50);
      setTimeout(scrollToTop, 200);
      setTimeout(scrollToTop, 500);
    }
  }, [nodes.length, currentRoot]);

  /**
   * Handle delete member
   */
  const handleDeleteMember = async (memberId) => {
    try {
      // Show loading spinner
      Swal.fire({
        title: "Deleting member...",
        html: '<div class="custom-spinner"></div>',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await apiService.deleteMember(slug, memberId);
      setEditingMember(null);

      if (onMemberUpdate) {
        onMemberUpdate({ deleted: true, id: memberId });
      }

      // Show success message
      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Family member has been removed",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      // Show error message
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: "Failed to delete member. Please try again.",
        confirmButtonColor: "#d33",
      });
    }
  };

  /**
   * Handle save new member
   */
  const handleSaveNewMember = async () => {
    console.log("Form data before validation:", newMemberData);

    // Trim and validate name
    const trimmedName = newMemberData.name?.trim();

    if (!trimmedName) {
      setAddMemberMessage({ type: "error", text: "Please enter a name" });
      // Scroll to top of modal to show error
      if (addMemberModalRef.current) {
        addMemberModalRef.current.scrollTop = 0;
      }
      return;
    }

    // Validate gender (required field)
    if (!newMemberData.gender) {
      setAddMemberMessage({ type: "error", text: "Please select a gender" });
      // Scroll to top of modal to show error
      if (addMemberModalRef.current) {
        addMemberModalRef.current.scrollTop = 0;
      }
      return;
    }

    if (!newMemberData.relationshipType) {
      setAddMemberMessage({
        type: "error",
        text: "Please select a relationship type",
      });
      // Scroll to top of modal to show error
      if (addMemberModalRef.current) {
        addMemberModalRef.current.scrollTop = 0;
      }
      return;
    }

    // Validate relatedMemberId exists
    if (!newMemberData.relatedMemberId) {
      setAddMemberMessage({
        type: "error",
        text: "Related member ID is missing",
      });
      // Scroll to top of modal to show error
      if (addMemberModalRef.current) {
        addMemberModalRef.current.scrollTop = 0;
      }
      return;
    }

    try {
      setAddMemberMessage({ type: "", text: "" });
      setIsLoading(true);

      // Build the base payload with required fields
      const payload = {
        name: trimmedName,
        gender: newMemberData.gender, // Required: "male" or "female"
      };

      // Add optional fields only if they have values
      if (newMemberData.birthDate) {
        payload.birthDate = newMemberData.birthDate;
      }
      if (newMemberData.birthPlace?.trim()) {
        payload.birthPlace = newMemberData.birthPlace.trim();
      }
      if (newMemberData.deathDate) {
        payload.deathDate = newMemberData.deathDate;
      }
      if (newMemberData.deathPlace?.trim()) {
        payload.deathPlace = newMemberData.deathPlace.trim();
      }
      if (newMemberData.occupation?.trim()) {
        payload.occupation = newMemberData.occupation.trim();
      }
      if (newMemberData.description?.trim()) {
        payload.description = newMemberData.description.trim();
      }

      // Add images if any were added
      if (newMemberData.images && newMemberData.images.length > 0) {
        payload.images = newMemberData.images.map((img) => ({
          url: img.url,
          type: img.type || "gallery",
          caption: img.caption || "",
          title: img.title || "",
          description: img.description || "",
        }));
        console.log("Adding images to payload:", payload.images);
      }

      // Add relationship based on type
      const relatedMemberId = newMemberData.relatedMemberId;

      switch (newMemberData.relationshipType) {
        case "spouse":
          // Spouse relationship
          payload.spouses = [{ id: relatedMemberId }];
          break;
        case "child":
          // This person is a child of the selected member
          payload.parents = [{ id: relatedMemberId, type: "blood" }];
          break;
        case "parent":
          // This person is a parent of the selected member
          // We'll add the selected member as a child
          payload.children = [{ id: relatedMemberId, type: "blood" }];
          break;
        case "sibling":
          // Sibling relationship
          payload.siblings = [{ id: relatedMemberId, type: "blood" }];
          break;
        default:
          setAddMemberMessage({
            type: "error",
            text: "Please select a valid relationship type",
          });
          return;
      }

      console.log("Final payload being sent:", payload);

      // Show loading indicator
      Swal.fire({
        title: "Adding Member...",
        html: '<div class="custom-spinner"></div>',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      setIsLoading(true);
      const response = await apiService.createMember(slug, payload);

      if (response?.data?.success) {
        setIsLoading(false);

        // Show success message
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: response.data.message || "Member added successfully!",
          timer: 2000,
          showConfirmButton: false,
        });

        // Wait for user to see success message
        setTimeout(() => {
          if (onMemberUpdate) {
            onMemberUpdate(response.data.data?.member || response.data.member);
          }
          resetAddMemberForm();
        }, 2000);
      } else {
        setIsLoading(false);
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: response?.data?.message || "Failed to add member",
          confirmButtonColor: "#d33",
        });
      }
    } catch (error) {
      setIsLoading(false);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.message || "Failed to add member. Please try again.",
        confirmButtonColor: "#d33",
      });
    }
  };

  /**
   * Calculate age from birth date
   */
  const calculateAge = (birthDate) => {
    try {
      const birth = new Date(birthDate);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birth.getDate())
      ) {
        age--;
      }

      return age;
    } catch (e) {
      return "?";
    }
  };

  /**
   * Get gender icon
   */
  const getGenderIcon = (gender) => {
    if (gender === "male") return "♂";
    if (gender === "female") return "♀";
    return "◎";
  };

  /**
   * Check if a member has a subtree (has children and at least one child has relationships)
   */
  const hasSubTree = useCallback(
    (memberId) => {
      const member = members.find((m) => m.id === memberId);
      if (!member || !member.children || member.children.length === 0) {
        return false;
      }

      // Check if any child has their own relationships (children or spouse)
      const hasChildWithRelationships = member.children.some((childRef) => {
        const childId = typeof childRef === "object" ? childRef.id : childRef;
        const child = members.find((m) => m.id === childId);
        if (!child) return false;

        // Check if child has children or spouse
        const hasChildren = child.children && child.children.length > 0;
        const hasSpouse =
          child.spouse || (child.spouses && child.spouses.length > 0);

        return hasChildren || hasSpouse;
      });

      return hasChildWithRelationships;
    },
    [members]
  );

  /**
   * Render custom node matching SVG FamilyNode styling
   */
  const renderNode = useCallback(
    (node) => {
      const member = node.data;
      // Find image with type="profile", fallback to first image
      const profileImageObj =
        member?.images?.find((img) => img.type === "profile") ||
        member?.images?.[0];
      const profileImage = profileImageObj?.url;
      const hasDeathDate = member?.deathDate && member?.deathDate.trim() !== "";
      const isSelected = selectedMemberId === node.id;
      const genderIcon = getGenderIcon(node.gender);
      const isPlaceholder = node.placeholder || false;
      const WIDTH = 260;
      const HEIGHT = 280;
      return (
        <div
          className="node-wrapper"
          style={{
            width: WIDTH,
            height: HEIGHT,
            transform: `translate(${node.left * (WIDTH / 2)}px, ${
              node.top * (HEIGHT / 2)
            }px)`,
          }}
          data-node-id={node.id}
        >
          <div
            className={`family-node ${isSelected ? "selected" : ""}`}
            onClick={() => setSelectedMemberId(node.id)}
          >
            <div className="node-card">
              {/* Background */}
              <div className="node-bg"></div>

              {/* Image or Avatar Container */}
              <div className="node-image-container">
                {profileImage ? (
                  <img
                    src={profileImage}
                    alt={member?.name}
                    className="node-image"
                  />
                ) : (
                  <div className="node-avatar">
                    <span className="avatar-placeholder"></span>
                  </div>
                )}

                {/* Gender Badge */}
                {genderIcon && (
                  <span className="gender-icon">{genderIcon}</span>
                )}
              </div>

              {/* Member Info */}
              <div className="node-info">
                <div className="node-name" title={member?.name}>
                  {member?.name?.substring(0, 18) || "Unknown"}
                </div>

                {/* Birth Date */}
                <div className="node-date birth-date">
                  📅{" "}
                  {member?.birthDate
                    ? new Date(member.birthDate).getFullYear()
                    : "—"}
                </div>

                {/* Death Date or Age */}
                {hasDeathDate ? (
                  <div className="node-date death-date">
                    ✝️ {new Date(member.deathDate).getFullYear()}
                  </div>
                ) : (
                  <div className="node-date age">
                    {member?.birthDate
                      ? `🎂 ${calculateAge(member.birthDate)}`
                      : "—"}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="node-actions">
                {!isPlaceholder && (
                  <button
                    className="action-btn edit-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingMember(member);
                    }}
                    title="Edit member"
                  >
                    <svg viewBox="0 0 24 24" className="btn-icon">
                      <path
                        d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  </button>
                )}

                {/* Gallery Button */}
                {(member?.images?.length || 0) > 0 && (
                  <button
                    className="action-btn gallery-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setGalleryMember(member);
                    }}
                    title="View gallery"
                  >
                    <svg viewBox="0 0 24 24" className="btn-icon">
                      <rect
                        x="3"
                        y="3"
                        width="18"
                        height="18"
                        rx="2"
                        ry="2"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                      <path
                        d="M21 15l-5-5L5 21"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  </button>
                )}

                {/* Image Count Badge */}
                {(member?.images?.length || 0) > 0 && (
                  <span className="image-badge">{member?.images.length}</span>
                )}
              </div>

              {/* Selection Border */}
              {isSelected && <div className="selection-border"></div>}
            </div>
          </div>

          {/* Subtree Button - show if member has subtree and is not the current root */}
          {!isPlaceholder && hasSubTree(node.id) && currentRoot !== node.id && (
            <button
              className="subtree-nav-btn"
              onClick={(e) => {
                e.stopPropagation();
                setRootId(node.id);
                setSelectedMemberId(node.id);
                searchBarRef.current?.reset();
              }}
              title="This member has a subtree"
            >
              🌳
            </button>
          )}

          {/* Add Member Button - show for all nodes */}
          <button
            className="add-member-btn"
            onClick={(e) => {
              e.stopPropagation();

              if (isPlaceholder) {
                // For placeholders, find closest real member to establish relationship
                let relatedMember = null;

                // First try children
                if (node.children?.length > 0) {
                  const firstChild = node.children[0];
                  relatedMember = members.find((m) => m.id === firstChild.id);
                }

                // If no children, try spouse
                if (!relatedMember && node.spouses?.length > 0) {
                  const firstSpouse = node.spouses[0];
                  relatedMember = members.find((m) => m.id === firstSpouse.id);
                }

                if (relatedMember) {
                  resetAddMemberForm();
                  setAddingMemberFor(relatedMember);
                  setNewMemberData({
                    name: "",
                    gender:
                      node.gender === "male"
                        ? "male"
                        : node.gender === "female"
                        ? "female"
                        : "",
                    birthDate: "",
                    birthPlace: "",
                    deathDate: "",
                    deathPlace: "",
                    occupation: "",
                    description: "",
                    relationshipType: "parent",
                    relatedMemberId: relatedMember.id,
                  });
                }
              } else {
                // For regular nodes
                resetAddMemberForm();
                setAddingMemberFor(member);
                setNewMemberData({
                  name: "",
                  gender: "",
                  birthDate: "",
                  birthPlace: "",
                  deathDate: "",
                  deathPlace: "",
                  occupation: "",
                  description: "",
                  relationshipType: "",
                  relatedMemberId: member.id,
                });
              }
            }}
            title="Add family member"
          >
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                d="M12 5v14M5 12h14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      );
    },
    [selectedMemberId, hasSubTree, members]
  );

  if (nodes.length === 0) {
    return (
      <div className="react-family-tree-view">
        <div className="empty-state">
          <p>No family members to display</p>
        </div>
      </div>
    );
  }

  return (
    <div className="react-family-tree-view" ref={containerRef}>
      {/* Toolbar */}
      {showToolbar && (
        <div className="tree-toolbar">
          {/* Meta info: slug and share link */}
          <div className="page-meta">
            {slug && (
              <div className="tree-meta-info">
                <div className="slug-container">
                  <span className="slug-label">Slug:</span>
                  <code className="slug-value">{slug}</code>
                </div>
                <div className="share-link-container">
                  <span className="share-label">Share:</span>
                  <div className="share-input-wrapper">
                    <code className="share-url">{getShareableUrl()}</code>
                    <div className="share-actions">
                      <button
                        className="btn-copy"
                        onClick={() => {
                          navigator.clipboard.writeText(getShareableUrl());
                          alert("Link copied to clipboard!");
                        }}
                        title="Copy link"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect
                            x="9"
                            y="9"
                            width="13"
                            height="13"
                            rx="2"
                            ry="2"
                          ></rect>
                          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
                        </svg>
                      </button>
                      <button
                        className="btn-help"
                        onClick={() => onShowHelp && onShowHelp()}
                        title="How to use this link"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          width="18"
                          height="18"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"></path>
                          <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="toolbar-controls">
            <SearchBar
              ref={searchBarRef}
              members={members}
              onSelectMember={(member) => {
                console.log("=== SEARCH MEMBER SELECTED ===");
                console.log(`Member: ${member.name} (${member.id})`);
                console.log(`Current root: ${currentRoot}`);

                // First select the member
                setSelectedMemberId(member.id);

                // Wait a bit for React to render, then check if node exists in DOM
                setTimeout(() => {
                  const nodeElement = document.querySelector(
                    `[data-node-id="${member.id}"]`
                  );

                  if (!nodeElement) {
                    // Node is not rendered in the tree, change root
                    console.log(
                      `❌ Node not found in DOM. Changing root to ${member.id}`
                    );
                    setRootId(member.id);

                    // Wait for tree to re-render with new root, then select and scroll
                    setTimeout(() => {
                      setSelectedMemberId(member.id);
                      console.log(`✓ Root changed and member selected`);

                      // Scroll to the newly rendered node
                      setTimeout(() => {
                        const newNodeElement = document.querySelector(
                          `[data-node-id="${member.id}"]`
                        );
                        if (newNodeElement) {
                          newNodeElement.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                            inline: "center",
                          });
                        }
                      }, 100);
                    }, 300);
                  } else {
                    // Node exists, scroll to it
                    console.log(`✓ Node found in DOM. Scrolling to view.`);
                    nodeElement.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                      inline: "center",
                    });
                  }
                }, 150);

                console.log("=== END SEARCH ===");
              }}
              placeholder="Search family members..."
            />
            <select
              value={currentRoot || ""}
              onChange={(e) => {
                setRootId(e.target.value);
                searchBarRef.current?.reset();
              }}
              className="root-selector"
            >
              <option value="">Select Root Person</option>
              {nodes.map((node) => (
                <option key={node.id} value={node.id}>
                  {node.data.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Family Tree */}
      <div className="tree-container">
        {currentRoot && (
          <PinchZoomPanContainer
            ref={pinchZoomRef}
            minScale={0.2}
            maxScale={2.5}
            initialScale={1}
          >
            <ReactFamilyTree
              nodes={nodes}
              rootId={currentRoot}
              width={WIDTH}
              height={HEIGHT}
              className="custom-tree"
              // placeholders={true}
              renderNode={renderNode}
            />
          </PinchZoomPanContainer>
        )}
      </div>

      {/* Member Editor Modal */}
      {editingMember && (
        <MemberEditor
          member={editingMember}
          onSave={(updatedMember) => {
            onMemberUpdate(updatedMember);
            setEditingMember(null);
          }}
          onDelete={handleDeleteMember}
          onCancel={() => setEditingMember(null)}
        />
      )}

      {/* Gallery Modal */}
      {galleryMember && (
        <ImageGallery
          member={galleryMember}
          images={[...(galleryMember.images || [])]}
          onClose={() => setGalleryMember(null)}
          onSave={(changes) => {
            // Build payload with only what changed
            const payload = {
              id: galleryMember.id,
            };

            // Add new images if any
            if (changes.images && changes.images.length > 0) {
              payload.images = changes.images;
            }

            // Add deleted image IDs if any
            if (changes.removeImages && changes.removeImages.length > 0) {
              payload.removeImages = changes.removeImages;
            }

            // Update profile image URL if changed
            if (changes.profileImageUrl !== undefined) {
              payload.profileImageUrl = changes.profileImageUrl;
            }

            // Update local state immediately
            setGalleryMember({
              ...galleryMember,
              images: changes.allImages,
              profileImageUrl: changes.profileImageUrl,
            });

            // Only call API if there are actual changes
            if (
              payload.images ||
              payload.removeImages ||
              payload.profileImageUrl !== undefined
            ) {
              console.log("Saving gallery changes:", payload);
              onMemberUpdate(payload);
            }
          }}
        />
      )}

      {/* Image Gallery for Add Member */}
      {showAddMemberGallery && (
        <div style={{ zIndex: 10100 }}>
          <ImageGallery
            member={{
              name: newMemberData.name || "New Member",
              images: newMemberData.images,
            }}
            images={newMemberData.images}
            onClose={() => setShowAddMemberGallery(false)}
            onAddImage={(image, allImages) => {
              if (allImages) {
                // Update all images (from toggle/type change)
                setNewMemberData({
                  ...newMemberData,
                  images: allImages,
                });
              } else if (image) {
                // Add single new image
                setNewMemberData({
                  ...newMemberData,
                  images: [...(newMemberData.images || []), image],
                });
              }
            }}
            onDeleteImage={(imageToDelete) => {
              setNewMemberData({
                ...newMemberData,
                images: (newMemberData.images || []).filter(
                  (img) => img.id !== imageToDelete.id
                ),
              });
            }}
          />
        </div>
      )}

      {/* Add Member Modal */}
      {addingMemberFor && (
        <div className="modal-overlay" onClick={resetAddMemberForm}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            ref={addMemberModalRef}
          >
            <div className="modal-header">
              <h2>Add Family Member</h2>
              <button className="modal-close" onClick={resetAddMemberForm}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              {addMemberMessage.text && (
                <div className={`${addMemberMessage.type}-message`}>
                  {addMemberMessage.text}
                </div>
              )}
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  placeholder="Enter full name"
                  value={newMemberData.name}
                  onChange={(e) =>
                    setNewMemberData({
                      ...newMemberData,
                      name: e.target.value,
                    })
                  }
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Gender *</label>
                <select
                  value={newMemberData.gender}
                  onChange={(e) =>
                    setNewMemberData({
                      ...newMemberData,
                      gender: e.target.value,
                    })
                  }
                  className="form-input"
                >
                  <option value="">-- Select Gender --</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div className="form-group">
                <label>Relationship Type *</label>
                <select
                  value={newMemberData.relationshipType}
                  onChange={(e) =>
                    setNewMemberData({
                      ...newMemberData,
                      relationshipType: e.target.value,
                    })
                  }
                  className="form-input"
                >
                  <option value="">-- Select Relationship --</option>
                  <option value="parent">
                    Parent of {addingMemberFor.name}
                  </option>
                  <option value="child">Child of {addingMemberFor.name}</option>
                  <option value="spouse">
                    Spouse of {addingMemberFor.name}
                  </option>
                  <option value="sibling">
                    Sibling of {addingMemberFor.name}
                  </option>
                </select>
              </div>
              <div className="form-group">
                <label>Birth Date</label>
                <input
                  type="date"
                  value={newMemberData.birthDate}
                  onChange={(e) =>
                    setNewMemberData({
                      ...newMemberData,
                      birthDate: e.target.value,
                    })
                  }
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Birth Place</label>
                <input
                  type="text"
                  placeholder="Enter birth place"
                  value={newMemberData.birthPlace}
                  onChange={(e) =>
                    setNewMemberData({
                      ...newMemberData,
                      birthPlace: e.target.value,
                    })
                  }
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Death Date</label>
                <input
                  type="date"
                  value={newMemberData.deathDate}
                  onChange={(e) =>
                    setNewMemberData({
                      ...newMemberData,
                      deathDate: e.target.value,
                    })
                  }
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Death Place</label>
                <input
                  type="text"
                  placeholder="Enter death place"
                  value={newMemberData.deathPlace}
                  onChange={(e) =>
                    setNewMemberData({
                      ...newMemberData,
                      deathPlace: e.target.value,
                    })
                  }
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Occupation</label>
                <input
                  type="text"
                  placeholder="Enter occupation"
                  value={newMemberData.occupation}
                  onChange={(e) =>
                    setNewMemberData({
                      ...newMemberData,
                      occupation: e.target.value,
                    })
                  }
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  placeholder="Enter description or notes"
                  value={newMemberData.description}
                  onChange={(e) =>
                    setNewMemberData({
                      ...newMemberData,
                      description: e.target.value,
                    })
                  }
                  className="form-input"
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Images</label>
                <button
                  type="button"
                  className="btn-gallery-add-member"
                  onClick={() => setShowAddMemberGallery(true)}
                >
                  🖼️ Manage Images
                  {newMemberData.images && newMemberData.images.length > 0 && (
                    <span
                      style={{
                        background: "var(--primary-color)",
                        color: "white",
                        padding: "0.25rem 0.5rem",
                        borderRadius: "12px",
                        fontSize: "0.85rem",
                        fontWeight: "bold",
                      }}
                    >
                      {newMemberData.images.length}
                    </span>
                  )}
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={resetAddMemberForm}
                title="Cancel"
                disabled={isLoading}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveNewMember}
                title="Save Member"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="spinner"></div>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Loading Overlay */}
      {(isLoading || parentLoading) && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner-large"></div>
            <p>Processing...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReactFamilyTreeView;
